import { useEffect, useState, useCallback, useRef } from 'react';
import {
  getServices, createService, updateService, deleteService,
  addServiceVariant, updateServiceVariant, deleteServiceVariant,
} from '../services/api';
import type { PriceType, ServiceVariantPayload } from '../services/api';
import { ImageUploadField } from '../components/ImageUploadField';
import { plainText } from '../utils/plainText';
import { HelpCircle } from 'lucide-react';
import GuidedTour, { TourStep } from '../components/GuidedTour';
import FloatingSaveBar from '../components/FloatingSaveBar';

const SERVICES_TOUR: TourStep[] = [
  { target: '[data-tour="service-new"]', title: 'Crea un servicio', body: 'Toca este botón verde para agregar un servicio nuevo a tu catálogo.', advanceOn: 'click' },
  { target: '[data-tour="service-nombre"]', title: 'Ponle un nombre', body: 'Escribe el nombre del servicio. Ej: "Corte de cabello", "Manicure", "Mantenimiento".' },
  { target: '[data-tour="service-precio"]', title: 'Precio base', body: 'Pon a cuánto cobras el servicio. Es lo que la IA le dirá al cliente.' },
  { target: '[data-tour="service-crear"]', title: 'Guarda el servicio', body: 'Toca "Crear servicio" y ¡listo! Ya queda en tu catálogo.', advanceOn: 'click' },
];

// ── Icons ─────────────────────────────────────────────────────────────────────
const PlusIcon   = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const EditIcon   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const TrashIcon  = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>);
const CloseIcon  = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);
const SaveIcon   = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>);
const WrenchIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>);
const ClockIcon  = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const TagIcon    = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>);

// ── Types ─────────────────────────────────────────────────────────────────────
interface ServiceVariant {
  variantId:        string;
  name:             string;
  description:      string | null;
  priceOverride:    string | null;
  priceModifier:    string | null;
  estimatedMinutes: number | null;
  sortOrder:        number;
  isActive:         boolean;
}

interface Service {
  serviceId:        string;
  name:             string;
  description:      string | null;
  category:         string | null;
  imageUrl:         string | null;
  priceType:        PriceType;
  basePrice:        string | null;
  minPrice:         string | null;
  maxPrice:         string | null;
  costPrice:        string | null;
  unitLabel:        string | null;
  hasVariants:      boolean;
  estimatedMinutes: number | null;
  customFields:     Record<string, any> | null;
  isActive:         boolean;
  variants:         ServiceVariant[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: string | number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n));

const fmtMinutes = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ''}` : `${m}min`;
};

const PRICE_TYPE_OPTIONS: { value: PriceType; label: string; hint: string }[] = [
  { value: 'FIXED',    label: 'Precio fijo',     hint: 'Un precio fijo por cada vez que se realiza' },
  { value: 'PER_HOUR', label: 'Por hora',         hint: 'Se multiplica por las horas trabajadas' },
  { value: 'PER_DAY',  label: 'Por día',          hint: 'Se multiplica por los días de servicio' },
  { value: 'PER_UNIT', label: 'Por unidad',       hint: 'Se multiplica por la cantidad de unidades' },
  { value: 'VARIABLE', label: 'Precio variable',  hint: 'Sin precio fijo — el admin cotiza cada caso' },
];

const SERVICE_TEMPLATES: Array<{
  name: string; category: string; description: string;
  priceType: PriceType; basePrice: number; estimatedMinutes: number;
}> = [
  // Barbería y estética
  { name: 'Corte de cabello',             category: 'Barbería',         description: 'Corte de cabello con tijeras o máquina',              priceType: 'FIXED',    basePrice: 20000,  estimatedMinutes: 30  },
  { name: 'Corte + Barba',               category: 'Barbería',         description: 'Corte de cabello y arreglo de barba',                 priceType: 'FIXED',    basePrice: 35000,  estimatedMinutes: 45  },
  { name: 'Manicure',                    category: 'Estética',         description: 'Limpieza y esmaltado de uñas de manos',               priceType: 'FIXED',    basePrice: 25000,  estimatedMinutes: 45  },
  { name: 'Tinte de cabello',            category: 'Estética',         description: 'Coloración completa con tinte profesional',           priceType: 'VARIABLE', basePrice: 80000,  estimatedMinutes: 90  },
  // Mantenimiento de celulares
  { name: 'Cambio de pantalla (celular)',category: 'Celulares',        description: 'Reemplazo de display según modelo',                   priceType: 'VARIABLE', basePrice: 80000,  estimatedMinutes: 60  },
  { name: 'Cambio de batería (celular)', category: 'Celulares',        description: 'Sustitución de batería original o compatible',        priceType: 'FIXED',    basePrice: 50000,  estimatedMinutes: 30  },
  { name: 'Limpieza interna (celular)',  category: 'Celulares',        description: 'Limpieza de placa y conectores internos',             priceType: 'FIXED',    basePrice: 30000,  estimatedMinutes: 30  },
  { name: 'Desbloqueo de red',           category: 'Celulares',        description: 'Desbloqueo de operador mediante software o IMEI',     priceType: 'FIXED',    basePrice: 25000,  estimatedMinutes: 20  },
  // Mantenimiento de computadores
  { name: 'Formateo + instalación SO',  category: 'Computadores',     description: 'Formateo completo e instalación de Windows o Linux',  priceType: 'FIXED',    basePrice: 60000,  estimatedMinutes: 90  },
  { name: 'Limpieza interna (PC/laptop)',category: 'Computadores',     description: 'Limpieza de polvo, ventiladores y disipadores',       priceType: 'FIXED',    basePrice: 40000,  estimatedMinutes: 45  },
  { name: 'Cambio de pasta térmica',    category: 'Computadores',     description: 'Sustitución de pasta térmica en procesador',          priceType: 'FIXED',    basePrice: 35000,  estimatedMinutes: 30  },
  { name: 'Cambio de disco SSD',        category: 'Computadores',     description: 'Instalación de SSD y migración de datos',             priceType: 'VARIABLE', basePrice: 80000,  estimatedMinutes: 60  },
  { name: 'Diagnóstico técnico',        category: 'Computadores',     description: 'Revisión completa del equipo e informe de fallas',    priceType: 'FIXED',    basePrice: 30000,  estimatedMinutes: 30  },
  // Taller mecánico / automotriz
  { name: 'Cambio de aceite y filtro',  category: 'Taller',           description: 'Cambio de aceite motor + filtro de aceite',           priceType: 'FIXED',    basePrice: 80000,  estimatedMinutes: 45  },
  { name: 'Revisión de frenos',         category: 'Taller',           description: 'Inspección y ajuste del sistema de frenos',           priceType: 'VARIABLE', basePrice: 60000,  estimatedMinutes: 60  },
  { name: 'Lavado y encerado',          category: 'Taller',           description: 'Lavado exterior + encerado manual del vehículo',      priceType: 'FIXED',    basePrice: 50000,  estimatedMinutes: 60  },
  { name: 'Diagnóstico computarizado',  category: 'Taller',           description: 'Lectura de códigos OBD y diagnóstico electrónico',    priceType: 'FIXED',    basePrice: 50000,  estimatedMinutes: 45  },
  { name: 'Cambio de llantas',          category: 'Taller',           description: 'Desmontaje y montaje de llantas',                     priceType: 'PER_UNIT', basePrice: 20000,  estimatedMinutes: 15  },
  // Limpieza y hogar
  { name: 'Limpieza residencial',       category: 'Limpieza',         description: 'Limpieza general de apartamento o casa',              priceType: 'PER_HOUR', basePrice: 25000,  estimatedMinutes: 120 },
  { name: 'Lavado de muebles',          category: 'Limpieza',         description: 'Lavado y desinfección de sofás o muebles tapizados',  priceType: 'PER_UNIT', basePrice: 40000,  estimatedMinutes: 60  },
  { name: 'Planchado de ropa',          category: 'Limpieza',         description: 'Servicio de planchado por hora',                      priceType: 'PER_HOUR', basePrice: 15000,  estimatedMinutes: 60  },
  // Fitness y bienestar
  { name: 'Entrenamiento personal',     category: 'Fitness',          description: 'Sesión de entrenamiento personalizado',               priceType: 'FIXED',    basePrice: 50000,  estimatedMinutes: 60  },
  { name: 'Plan mensual fitness',       category: 'Fitness',          description: 'Seguimiento y rutinas durante un mes',                priceType: 'FIXED',    basePrice: 300000, estimatedMinutes: 0   },
  { name: 'Masaje relajante',           category: 'Bienestar',        description: 'Masaje corporal de relajación completa',              priceType: 'FIXED',    basePrice: 70000,  estimatedMinutes: 60  },
  // Consultoría y servicios digitales
  { name: 'Consultoría (por hora)',     category: 'Consultoría',      description: 'Asesoría profesional facturada por hora',             priceType: 'PER_HOUR', basePrice: 80000,  estimatedMinutes: 60  },
  { name: 'Diseño de logo',             category: 'Diseño',           description: 'Diseño de identidad visual + archivos editables',     priceType: 'VARIABLE', basePrice: 150000, estimatedMinutes: 0   },
  { name: 'Gestión redes sociales',     category: 'Marketing',        description: 'Administración mensual de redes sociales',            priceType: 'FIXED',    basePrice: 200000, estimatedMinutes: 0   },
];

const PRICE_TYPE_COLORS: Record<PriceType, string> = {
  FIXED:    'bg-blue-50 text-blue-700',
  PER_HOUR: 'bg-purple-50 text-purple-700',
  PER_DAY:  'bg-indigo-50 text-indigo-700',
  PER_UNIT: 'bg-teal-50 text-teal-700',
  VARIABLE: 'bg-orange-50 text-orange-600',
};

const PRICE_TYPE_LABELS: Record<PriceType, string> = {
  FIXED:    'Fijo',
  PER_HOUR: 'Por hora',
  PER_DAY:  'Por día',
  PER_UNIT: 'Por unidad',
  VARIABLE: 'Variable',
};

// ── Función para calcular precio efectivo de variante ─────────────────────────
function resolveVariantPrice(service: Service, variant: ServiceVariant): string {
  if (variant.priceOverride) return fmt(variant.priceOverride);
  if (variant.priceModifier && service.basePrice) {
    const mod = Number(variant.priceModifier);
    const base = Number(service.basePrice);
    return `${fmt(base * (1 + mod / 100))} (${mod > 0 ? '+' : ''}${mod}%)`;
  }
  return service.basePrice ? fmt(service.basePrice) : '—';
}

// ── Service Card ──────────────────────────────────────────────────────────────
function ServiceCard({ service, onEdit, onDelete }: {
  service:  Service;
  onEdit:   (s: Service) => void;
  onDelete: (s: Service) => void;
}) {
  const activeVariants = service.variants?.filter(v => v.isActive) ?? [];

  const priceDisplay = () => {
    if (service.priceType === 'VARIABLE') {
      const rango = service.minPrice && service.maxPrice
        ? `${fmt(service.minPrice)} – ${fmt(service.maxPrice)}`
        : null;
      return (
        <div>
          <span className="text-base font-bold text-orange-500">Cotización</span>
          {rango && <p className="text-xs text-txt-tertiary mt-0.5">{rango}</p>}
        </div>
      );
    }
    if (!service.basePrice) return <span className="text-txt-tertiary text-sm">Sin precio</span>;
    const unidad = service.unitLabel ? `/${service.unitLabel}` : '';
    return (
      <div>
        <span className="text-xl font-bold text-txt-primary">{fmt(service.basePrice)}</span>
        <span className="text-xs text-txt-tertiary ml-1">{unidad}</span>
      </div>
    );
  };

  return (
    <div className="bg-surface rounded-2xl border border-border-subtle shadow-sm overflow-hidden flex flex-col hover:shadow-md transition">
      {/* Imagen o placeholder */}
      {service.imageUrl ? (
        <div className="h-36 overflow-hidden">
          <img src={service.imageUrl} alt={service.name}
            className="w-full h-full object-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
      ) : (
        <div className="h-20 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
          <span className="text-txt-disabled"><WrenchIcon /></span>
        </div>
      )}

      <div className="p-4 flex-1 flex flex-col gap-2">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-txt-primary leading-tight truncate">{service.name}</p>
            {service.category && (
              <span className="inline-flex items-center gap-1 text-xs text-txt-secondary mt-0.5">
                <TagIcon /> {service.category}
              </span>
            )}
          </div>
          <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${PRICE_TYPE_COLORS[service.priceType]}`}>
            {PRICE_TYPE_LABELS[service.priceType]}
          </span>
        </div>

        {/* Descripción */}
        {service.description && (
          <p className="text-xs text-txt-secondary line-clamp-2">{plainText(service.description)}</p>
        )}

        {/* Precio */}
        <div className="mt-auto pt-2 border-t border-slate-50">
          {priceDisplay()}
        </div>

        {/* Duración */}
        {service.estimatedMinutes && (
          <div className="flex items-center gap-1 text-xs text-txt-secondary">
            <ClockIcon /> {fmtMinutes(service.estimatedMinutes)}
          </div>
        )}

        {/* Variantes */}
        {service.hasVariants && activeVariants.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {activeVariants.slice(0, 3).map(v => (
              <span key={v.variantId}
                className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full border border-purple-100">
                {v.name}
              </span>
            ))}
            {activeVariants.length > 3 && (
              <span className="text-xs bg-surface-overlay text-txt-tertiary px-2 py-0.5 rounded-full">
                +{activeVariants.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Acciones */}
        <div className="flex gap-2 mt-1">
          <button onClick={() => onEdit(service)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium text-txt-secondary bg-surface-overlay hover:bg-border-default transition">
            <EditIcon /> Editar
          </button>
          <button onClick={() => onDelete(service)}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-red-400 bg-red-50 hover:bg-red-100 transition">
            <TrashIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Variant Row ───────────────────────────────────────────────────────────────
function VariantRow({ variant, service, onUpdate, onRemove }: {
  variant:  ServiceVariant;
  service:  Service;
  onUpdate: (id: string, data: Partial<ServiceVariantPayload>) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({
    name:             variant.name,
    description:      variant.description ?? '',
    priceOverride:    variant.priceOverride ?? '',
    priceModifier:    variant.priceModifier ?? '',
    estimatedMinutes: variant.estimatedMinutes ? String(variant.estimatedMinutes) : '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return;
    if (form.priceOverride && form.priceModifier) return; // no pueden ir juntos
    setSaving(true);
    await onUpdate(variant.variantId, {
      name:             form.name.trim(),
      description:      form.description.trim() || undefined,
      priceOverride:    form.priceOverride ? Number(form.priceOverride) : undefined,
      priceModifier:    form.priceModifier ? Number(form.priceModifier) : undefined,
      estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : undefined,
    });
    setSaving(false);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-surface-elevated transition">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-txt-primary truncate">{variant.name}</p>
          {variant.description && (
            <p className="text-xs text-txt-tertiary truncate">{variant.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3 ml-2 flex-shrink-0">
          <span className="text-sm font-semibold text-txt-primary">
            {resolveVariantPrice(service, variant)}
          </span>
          {variant.estimatedMinutes && (
            <span className="flex items-center gap-0.5 text-xs text-txt-tertiary">
              <ClockIcon /> {fmtMinutes(variant.estimatedMinutes)}
            </span>
          )}
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-txt-tertiary hover:text-blue-600 hover:bg-blue-50 transition">
              <EditIcon />
            </button>
            <button onClick={() => onRemove(variant.variantId)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-txt-tertiary hover:text-red-500 hover:bg-red-50 transition">
              <TrashIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const bothPrices = !!(form.priceOverride && form.priceModifier);

  return (
    <div className="px-3 py-3 bg-blue-50/40 rounded-xl space-y-2 border border-blue-100">
      <div className="grid grid-cols-2 gap-2">
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="Nombre *"
          className="col-span-2 px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Descripción (opcional)"
          className="col-span-2 px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <div>
          <label className="text-xs text-txt-secondary block mb-0.5">Precio fijo (override)</label>
          <input value={form.priceOverride} onChange={e => setForm(f => ({ ...f, priceOverride: e.target.value, priceModifier: '' }))}
            type="number" placeholder="Ej: 80000"
            className={`w-full px-2 py-1.5 text-xs border bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30 ${bothPrices ? 'border-red-400' : 'border-border-default'}`} />
        </div>
        <div>
          <label className="text-xs text-txt-secondary block mb-0.5">% sobre precio base</label>
          <input value={form.priceModifier} onChange={e => setForm(f => ({ ...f, priceModifier: e.target.value, priceOverride: '' }))}
            type="number" placeholder="Ej: 30 (+30%)"
            className={`w-full px-2 py-1.5 text-xs border bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30 ${bothPrices ? 'border-red-400' : 'border-border-default'}`} />
        </div>
        <input value={form.estimatedMinutes} onChange={e => setForm(f => ({ ...f, estimatedMinutes: e.target.value }))}
          type="number" placeholder="Duración (minutos)"
          className="px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
      </div>
      {bothPrices && (
        <p className="text-xs text-red-500">Solo usa precio fijo O porcentaje, no ambos.</p>
      )}
      <div className="flex gap-1">
        <button onClick={save} disabled={saving || !form.name.trim() || bothPrices}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium text-[#0A0A0F] disabled:opacity-50 transition"
          style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}> 
          {saving ? <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg> : <><SaveIcon /> Guardar</>}
        </button>
        <button onClick={() => setEditing(false)}
          className="px-3 py-1.5 rounded-lg text-xs text-txt-secondary hover:bg-border-default transition">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Service Modal ─────────────────────────────────────────────────────────────
interface ServiceForm {
  name:             string;
  description:      string;
  category:         string;
  imageUrl:         string;
  priceType:        PriceType;
  basePrice:        string;
  minPrice:         string;
  maxPrice:         string;
  costPrice:        string;
  unitLabel:        string;
  hasVariants:      boolean;
  estimatedMinutes: string;
}

const EMPTY_SERVICE_FORM: ServiceForm = {
  name: '', description: '', category: '', imageUrl: '',
  priceType: 'FIXED', basePrice: '', minPrice: '', maxPrice: '',
  costPrice: '', unitLabel: '', hasVariants: false, estimatedMinutes: '',
};

const EMPTY_VARIANT_FORM = {
  name: '', description: '', priceOverride: '', priceModifier: '', estimatedMinutes: '',
};

function ServiceModal({ service, onClose, onSaved }: {
  service:  Service | null;
  onClose:  () => void;
  onSaved:  (s: Service) => void;
}) {
  const isEdit = !!service;

  const [form, setForm] = useState<ServiceForm>(service ? {
    name:             service.name,
    description:      service.description ?? '',
    category:         service.category ?? '',
    imageUrl:         service.imageUrl ?? '',
    priceType:        service.priceType,
    basePrice:        service.basePrice ?? '',
    minPrice:         service.minPrice ?? '',
    maxPrice:         service.maxPrice ?? '',
    costPrice:        service.costPrice ?? '',
    unitLabel:        service.unitLabel ?? '',
    hasVariants:      service.hasVariants,
    estimatedMinutes: service.estimatedMinutes ? String(service.estimatedMinutes) : '',
  } : EMPTY_SERVICE_FORM);

  const [variants,      setVariants]     = useState<ServiceVariant[]>(service?.variants ?? []);
  const [newVariant,    setNewVariant]   = useState(EMPTY_VARIANT_FORM);
  const [addingVar,     setAddingVar]    = useState(false);
  const [saving,        setSaving]       = useState(false);
  const [saved,         setSaved]        = useState(false);
  const [error,         setError]        = useState('');
  const [tab,           setTab]          = useState<'info' | 'variants'>('info');

  // Custom fields
  const [cfKey,     setCfKey]    = useState('');
  const [cfValue,   setCfValue]  = useState('');
  const [cfFields,  setCfFields] = useState<Record<string, string>>(
    service?.customFields ? Object.fromEntries(
      Object.entries(service.customFields).map(([k, v]) => [k, String(v)])
    ) : {}
  );

  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setTimeout(() => nameRef.current?.focus(), 50); }, []);

  // Snapshot inicial para detectar cambios sin guardar (botón flotante)
  const baselineRef = useRef('');
  useEffect(() => { baselineRef.current = JSON.stringify({ form, cfFields }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = baselineRef.current !== '' && JSON.stringify({ form, cfFields }) !== baselineRef.current;

  const set = (k: keyof ServiceForm, v: any) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const selectedPriceType = PRICE_TYPE_OPTIONS.find(o => o.value === form.priceType);

  // ── Guardar servicio ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.name.trim()) return setError('El nombre es requerido');
    if (form.priceType !== 'VARIABLE' && !form.basePrice) return setError('El precio base es requerido para este tipo de precio');
    setSaving(true); setError('');
    try {
      const payload: any = {
        name:             form.name.trim(),
        description:      form.description.trim() || undefined,
        category:         form.category.trim() || undefined,
        imageUrl:         form.imageUrl.trim() || undefined,
        priceType:        form.priceType,
        basePrice:        form.basePrice ? Number(form.basePrice) : undefined,
        minPrice:         form.minPrice ? Number(form.minPrice) : undefined,
        maxPrice:         form.maxPrice ? Number(form.maxPrice) : undefined,
        costPrice:        form.costPrice ? Number(form.costPrice) : undefined,
        unitLabel:        form.unitLabel.trim() || undefined,
        hasVariants:      form.hasVariants,
        estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : undefined,
        customFields:     Object.keys(cfFields).length > 0 ? cfFields : undefined,
      };
      const res = isEdit
        ? await updateService(service!.serviceId, payload)
        : await createService(payload);
      onSaved({ ...res.data, variants });
      onClose();
    } catch {
      setError('Error al guardar. Verifica los datos.');
    } finally {
      setSaving(false);
    }
  };

  // ── Variantes ─────────────────────────────────────────────────────────────
  const handleAddVariant = async () => {
    if (!isEdit || !newVariant.name.trim()) return;
    if (newVariant.priceOverride && newVariant.priceModifier) return setError('Una variante no puede tener precio fijo y porcentaje al mismo tiempo');
    setAddingVar(true); setError('');
    try {
      const res = await addServiceVariant(service!.serviceId, {
        name:             newVariant.name.trim(),
        description:      newVariant.description.trim() || undefined,
        priceOverride:    newVariant.priceOverride ? Number(newVariant.priceOverride) : undefined,
        priceModifier:    newVariant.priceModifier ? Number(newVariant.priceModifier) : undefined,
        estimatedMinutes: newVariant.estimatedMinutes ? Number(newVariant.estimatedMinutes) : undefined,
      });
      setVariants(v => [...v, res.data]);
      setNewVariant(EMPTY_VARIANT_FORM);
    } catch {
      setError('Error al agregar variante');
    } finally {
      setAddingVar(false);
    }
  };

  const handleUpdateVariant = async (variantId: string, data: Partial<ServiceVariantPayload>) => {
    const res = await updateServiceVariant(variantId, data);
    setVariants(v => v.map(x => x.variantId === variantId ? res.data : x));
  };

  const handleRemoveVariant = async (variantId: string) => {
    await deleteServiceVariant(variantId);
    setVariants(v => v.filter(x => x.variantId !== variantId));
  };

  // ── Custom fields ─────────────────────────────────────────────────────────
  const addCf = () => {
    if (!cfKey.trim() || !cfValue.trim()) return;
    setCfFields(f => ({ ...f, [cfKey.trim()]: cfValue.trim() }));
    setCfKey(''); setCfValue('');
  };

  const removeCf = (key: string) => {
    const updated = { ...cfFields };
    delete updated[key];
    setCfFields(updated);
  };

  // ── Margen live ───────────────────────────────────────────────────────────
  const liveMargin = form.basePrice && form.costPrice
    ? Math.round(((Number(form.basePrice) - Number(form.costPrice)) / Number(form.basePrice)) * 100)
    : null;

  // ── Servicio ficticio para VariantRow (necesita service para resolver precio) ──
  const serviceForVariant: Service = {
    ...(service ?? {} as any),
    priceType: form.priceType,
    basePrice: form.basePrice || null,
    unitLabel: form.unitLabel || null,
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4">
      <div className="bg-surface rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-subtle flex items-center justify-between sticky top-0 bg-surface z-10">
          <h2 className="font-bold text-txt-primary">{isEdit ? 'Editar servicio' : 'Nuevo servicio'}</h2>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-txt-tertiary hover:bg-surface-overlay transition">
            <CloseIcon />
          </button>
        </div>

        {/* Tabs */}
        {isEdit && (
          <div className="flex border-b border-border-subtle px-6">
            {(['info', 'variants'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-txt-tertiary hover:text-txt-secondary'}`}>
                {t === 'info' ? 'Información' : `Variantes (${variants.filter(v => v.isActive).length})`}
              </button>
            ))}
          </div>
        )}

        <div className="px-6 py-5 space-y-4">

          {/* ── TAB INFO ─────────────────────────────────────────────────── */}
          {tab === 'info' && (
            <>
              <ImageUploadField
                value={form.imageUrl}
                onChange={url => set('imageUrl', url)}
                label="Imagen del servicio"
              />

              {/* Nombre */}
              <div data-tour="service-nombre">
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Nombre *</label>
                <input ref={nameRef} type="text" value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Ej: Corte de cabello, Instalación de paneles, Bartender"
                  className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
              </div>

              {/* Categoría */}
              <div>
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1 flex items-center gap-1">
                  <TagIcon /> Categoría
                </label>
                <input type="text" value={form.category}
                  onChange={e => set('category', e.target.value)}
                  placeholder="Ej: Cortes, Reparaciones, Eventos"
                  className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
              </div>

              {/* Descripción */}
              <div>
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Descripción</label>
                <textarea value={form.description}
                  onChange={e => set('description', e.target.value)}
                  rows={3} maxLength={50000}
                  placeholder="Describe el servicio para que la IA lo pueda explicar a los clientes..."
                  className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition resize-none" />
                <p className="text-xs text-txt-tertiary text-right mt-0.5">{form.description.length.toLocaleString()} / 50.000</p>
              </div>

              {/* Tipo de precio */}
              <div>
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-2">Modelo de precio *</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {PRICE_TYPE_OPTIONS.map(opt => (
                    <label key={opt.value}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition ${form.priceType === opt.value ? 'border-blue-500 bg-blue-500/10' : 'border-border-default hover:border-border-default/70'}`}>
                      <input type="radio" name="priceType" value={opt.value}
                        checked={form.priceType === opt.value}
                        onChange={() => set('priceType', opt.value)}
                        className="text-blue-600" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-txt-primary">{opt.label}</span>
                        <p className="text-xs text-txt-tertiary">{opt.hint}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PRICE_TYPE_COLORS[opt.value]}`}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Precio base + unidad */}
              {form.priceType !== 'VARIABLE' && (
                <div className="grid grid-cols-2 gap-3">
                  <div data-tour="service-precio">
                    <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">
                      Precio base *
                      {form.priceType !== 'FIXED' && form.unitLabel && (
                        <span className="ml-1 font-normal normal-case text-blue-600">/{form.unitLabel}</span>
                      )}
                    </label>
                    <input type="number" value={form.basePrice}
                      onChange={e => set('basePrice', e.target.value)}
                      placeholder="0"
                      className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  </div>
                  {form.priceType !== 'FIXED' && (
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">
                        Etiqueta de unidad
                      </label>
                      <input type="text" value={form.unitLabel}
                        onChange={e => set('unitLabel', e.target.value)}
                        placeholder="hora, día, m², panel, persona"
                        className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                    </div>
                  )}
                </div>
              )}

              {/* Rango de referencia (VARIABLE) */}
              {form.priceType === 'VARIABLE' && (
                <div className="space-y-2">
                  <p className="text-xs text-txt-secondary flex items-center gap-1">
                    Rango de referencia (opcional — orienta al admin al cotizar)
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Precio mínimo</label>
                      <input type="number" value={form.minPrice}
                        onChange={e => set('minPrice', e.target.value)}
                        placeholder="50000"
                        className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Precio máximo</label>
                      <input type="number" value={form.maxPrice}
                        onChange={e => set('maxPrice', e.target.value)}
                        placeholder="200000"
                        className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                    </div>
                  </div>
                </div>
              )}

              {/* Costo + margen */}
              <div>
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">
                  Precio de costo <span className="font-normal normal-case">(para analytics)</span>
                </label>
                <input type="number" value={form.costPrice}
                  onChange={e => set('costPrice', e.target.value)}
                  placeholder="Costo interno del servicio"
                  className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                {liveMargin !== null && (
                  <p className={`text-xs font-semibold mt-1 ${liveMargin >= 40 ? 'text-green-600' : liveMargin >= 20 ? 'text-amber-600' : 'text-red-500'}`}>
                    Margen estimado: {liveMargin}%
                  </p>
                )}
              </div>

              {/* Duración estimada */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1 flex items-center gap-1">
                    <ClockIcon /> Duración estimada (min)
                  </label>
                  <input type="number" value={form.estimatedMinutes}
                    onChange={e => set('estimatedMinutes', e.target.value)}
                    placeholder="Ej: 60 = 1h, 90 = 1h 30min"
                    className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  {form.estimatedMinutes && Number(form.estimatedMinutes) > 0 && (
                    <p className="text-xs text-blue-600 mt-0.5">{fmtMinutes(Number(form.estimatedMinutes))}</p>
                  )}
                </div>

                {/* Tiene variantes */}
                <div className="flex flex-col justify-end pb-1">
                  <div className="flex items-center justify-between py-2 px-3 rounded-xl border border-border-default bg-surface-elevated">
                    <span className="text-xs text-txt-secondary font-medium">Tiene variantes</span>
                    <button type="button" onClick={() => set('hasVariants', !form.hasVariants)}
                      className="w-10 h-5 rounded-full transition relative flex-shrink-0"
                      style={form.hasVariants ? { background: 'linear-gradient(135deg,#9333ea,#2563eb)' } : { background: '#e2e8f0' }}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-surface rounded-full shadow transition-all ${form.hasVariants ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                  {form.hasVariants && (
                    <p className="text-xs text-txt-tertiary mt-1">Ej: Básico/Premium, 2h/4h/Full day</p>
                  )}
                </div>
              </div>

              {/* Campos personalizados */}
              <div className="space-y-2 border border-border-default rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide">
                    Campos personalizados
                  </label>
                  <span className="text-xs text-txt-tertiary">Para la IA y el equipo</span>
                </div>
                <div className="flex flex-wrap gap-1.5 min-h-6">
                  {Object.entries(cfFields).map(([k, v]) => (
                    <span key={k}
                      className="inline-flex items-center gap-1 text-xs bg-surface-overlay text-txt-secondary px-2 py-1 rounded-lg">
                      <span className="font-medium">{k}:</span> {v}
                      <button onClick={() => removeCf(k)} className="hover:text-red-500 ml-0.5">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input value={cfKey} onChange={e => setCfKey(e.target.value)}
                    placeholder="Campo (requiereVisita)"
                    className="flex-1 px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                  <input value={cfValue} onChange={e => setCfValue(e.target.value)}
                    placeholder="Valor (true)"
                    className="flex-1 px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                  <button onClick={addCf} type="button"
                    className="px-2 py-1.5 text-xs bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition">
                    +
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button onClick={handleSubmit} disabled={saving} data-tour="service-crear"
                className="w-full py-2.5 rounded-xl text-sm font-medium text-[#0A0A0F] disabled:opacity-50 transition"
                style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}>
                {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear servicio'}
              </button>
            </>
          )}

          {/* ── TAB VARIANTES ──────────────────────────────────────────────── */}
          {tab === 'variants' && (
            <>
              <div className="text-xs text-txt-tertiary space-y-0.5">
                <p>Cada variante puede tener su propio precio o un porcentaje sobre el precio base.</p>
                <p className="text-blue-600">
                  {selectedPriceType?.hint}
                  {form.basePrice && ` — Precio base: ${fmt(form.basePrice)}`}
                </p>
              </div>

              {variants.filter(v => v.isActive).length === 0 ? (
                <p className="text-center text-txt-tertiary text-sm py-6">Sin variantes aún</p>
              ) : (
                <div className="space-y-1">
                  {variants.filter(v => v.isActive).map(v => (
                    <VariantRow key={v.variantId} variant={v} service={serviceForVariant}
                      onUpdate={handleUpdateVariant}
                      onRemove={handleRemoveVariant} />
                  ))}
                </div>
              )}

              {/* Nueva variante */}
              <div className="border-t border-border-subtle pt-4 space-y-2">
                <p className="text-xs font-semibold text-txt-secondary uppercase tracking-wide">Nueva variante</p>
                <div className="grid grid-cols-2 gap-2">
                  <input value={newVariant.name}
                    onChange={e => setNewVariant(v => ({ ...v, name: e.target.value }))}
                    placeholder="Nombre * (ej: Básica, Express, Premium)"
                    className="col-span-2 px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <input value={newVariant.description}
                    onChange={e => setNewVariant(v => ({ ...v, description: e.target.value }))}
                    placeholder="Descripción (opcional)"
                    className="col-span-2 px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <div>
                    <label className="text-xs text-txt-secondary block mb-1">Precio fijo</label>
                    <input value={newVariant.priceOverride}
                      onChange={e => setNewVariant(v => ({ ...v, priceOverride: e.target.value, priceModifier: '' }))}
                      type="number" placeholder="Ej: 80000"
                      className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  </div>
                  <div>
                    <label className="text-xs text-txt-secondary block mb-1">% sobre base</label>
                    <input value={newVariant.priceModifier}
                      onChange={e => setNewVariant(v => ({ ...v, priceModifier: e.target.value, priceOverride: '' }))}
                      type="number" placeholder="Ej: 30 (+30%)"
                      className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  </div>
                  <input value={newVariant.estimatedMinutes}
                    onChange={e => setNewVariant(v => ({ ...v, estimatedMinutes: e.target.value }))}
                    type="number" placeholder="Duración (min)"
                    className="px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                </div>

                {error && <p className="text-xs text-red-500">{error}</p>}

                <button onClick={handleAddVariant}
                  disabled={addingVar || !newVariant.name.trim()}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-[#0A0A0F] disabled:opacity-40 transition"
                  style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}> 
                  {addingVar ? 'Agregando...' : <><PlusIcon /> Agregar variante</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {tab === 'info' && (
        <FloatingSaveBar
          dirty={dirty}
          saving={saving}
          saved={saved}
          onSave={handleSubmit}
          label={isEdit ? 'Guardar cambios' : 'Crear servicio'}
        />
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Services() {
  const [services,       setServices]      = useState<Service[]>([]);
  const [loading,        setLoading]       = useState(true);
  const [search,         setSearch]        = useState('');
  const [filterType,     setFilterType]    = useState<PriceType | 'all'>('all');
  const [filterCat,      setFilterCat]     = useState('');
  const [modalService,   setModalService]  = useState<Service | null | 'new'>(null);
  const [deleteTarget,   setDeleteTarget]  = useState<Service | null>(null);
  const [deleting,       setDeleting]      = useState(false);
  const [tourRun,        setTourRun]       = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('tour-service-done')) setTourRun(true);
  }, []);
  const [showTemplates,    setShowTemplates]    = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getServices()
      .then(res => setServices(res.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApplyTemplate = async (tpl: typeof SERVICE_TEMPLATES[0]) => {
    setApplyingTemplate(tpl.name);
    try {
      await createService({
        name:             tpl.name,
        category:         tpl.category,
        description:      tpl.description,
        priceType:        tpl.priceType,
        basePrice:        tpl.basePrice,
        estimatedMinutes: tpl.estimatedMinutes,
      });
      await load();
      setShowTemplates(false);
    } catch {
      // ignore if already exists
    } finally {
      setApplyingTemplate(null);
    }
  };

  const handleSaved = (saved: Service) => {
    setServices(prev => {
      const exists = prev.find(s => s.serviceId === saved.serviceId);
      return exists
        ? prev.map(s => s.serviceId === saved.serviceId ? saved : s)
        : [saved, ...prev];
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await deleteService(deleteTarget.serviceId);
    setServices(prev => prev.filter(s => s.serviceId !== deleteTarget.serviceId));
    setDeleteTarget(null);
    setDeleting(false);
  };

  // Categorías únicas para filtro
  const categories = Array.from(new Set(services.map(s => s.category).filter(Boolean))) as string[];

  const filtered = services.filter(s => {
    const q           = search.toLowerCase();
    const matchSearch = !q ||
      s.name.toLowerCase().includes(q) ||
      (s.category ?? '').toLowerCase().includes(q) ||
      (s.description ?? '').replace(/<[^>]+>/g, ' ').toLowerCase().includes(q);
    const matchType = filterType === 'all' || s.priceType === filterType;
    const matchCat  = !filterCat || s.category === filterCat;
    return matchSearch && matchType && matchCat;
  });

  return (
    <div className="min-h-screen bg-canvas">
      {/* Header */}
      <div className="bg-surface border-b border-border-subtle px-6 py-5 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <div>
              <h1 className="text-xl font-bold text-txt-primary">Servicios</h1>
              <p className="text-sm text-txt-tertiary mt-0.5">
                {loading ? '...' : `${services.length} servicio${services.length !== 1 ? 's' : ''} activo${services.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <button
              onClick={() => setShowTemplates(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border-default text-txt-secondary text-sm font-semibold hover:bg-surface-elevated transition"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
              </svg>
              Plantillas
            </button>
            <button onClick={() => setModalService('new')} data-tour="service-new"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-[#0A0A0F]"
              style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}>
              <PlusIcon /> Nuevo servicio
            </button>
            <button onClick={() => setTourRun(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-border-default text-txt-secondary hover:bg-surface-overlay transition"
              title="Ver tutorial paso a paso">
              <HelpCircle size={15} /> ¿Cómo funciona?
            </button>
          </div>

          {/* Filtros */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-tertiary" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por nombre o descripción..."
                className="pl-9 pr-4 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition w-64" />
            </div>
            <select value={filterType} onChange={e => setFilterType(e.target.value as any)}
              className="px-3 py-2 text-sm border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 bg-surface text-txt-secondary">
              <option value="all">Todos los tipos</option>
              {PRICE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {categories.length > 0 && (
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
                className="px-3 py-2 text-sm border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 bg-surface text-txt-secondary">
                <option value="">Todas las categorías</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {(search || filterType !== 'all' || filterCat) && (
              <button onClick={() => { setSearch(''); setFilterType('all'); setFilterCat(''); }}
                className="text-xs text-blue-600 hover:underline">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <svg className="animate-spin text-blue-600" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-txt-tertiary">
            <WrenchIcon />
            <p className="text-sm">
              {search || filterType !== 'all' || filterCat
                ? 'Sin resultados para este filtro'
                : 'Sin servicios aún'}
            </p>
            {!search && filterType === 'all' && !filterCat && (
              <button onClick={() => setModalService('new')}
                className="mt-1 px-5 py-2.5 rounded-xl text-[#0A0A0F] text-sm font-semibold"
                style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}> 
                Crear primer servicio
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(s => (
              <ServiceCard key={s.serviceId} service={s}
                onEdit={svc => setModalService(svc)}
                onDelete={setDeleteTarget} />
            ))}
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      {modalService !== null && (
        <ServiceModal
          service={modalService === 'new' ? null : modalService}
          onClose={() => setModalService(null)}
          onSaved={handleSaved}
        />
      )}

      <GuidedTour
        steps={SERVICES_TOUR}
        run={tourRun}
        onFinish={() => { setTourRun(false); localStorage.setItem('tour-service-done', '1'); }}
      />

      {/* Confirm eliminar */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4">
          <div className="bg-surface rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-txt-primary mb-2">¿Eliminar servicio?</h3>
            <p className="text-sm text-txt-secondary mb-5">
              <span className="font-semibold">{deleteTarget.name}</span> será desactivado junto con todas sus variantes.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2 rounded-xl text-sm font-medium bg-surface-overlay text-txt-secondary hover:bg-border-default transition">
                Cancelar
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2 rounded-xl text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition disabled:opacity-50">
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTemplates && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
              <div>
                <h2 className="text-base font-bold text-txt-primary">Plantillas de servicios</h2>
                <p className="text-xs text-txt-tertiary mt-0.5">Barbería, celulares, taller, limpieza, fitness y más — un clic para agregar</p>
              </div>
              <button
                onClick={() => setShowTemplates(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-txt-tertiary hover:bg-surface-overlay"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="overflow-y-auto p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SERVICE_TEMPLATES.map(tpl => {
                const isApplying = applyingTemplate === tpl.name;
                return (
                  <button
                    key={tpl.name}
                    onClick={() => handleApplyTemplate(tpl)}
                    disabled={isApplying}
                    className="text-left p-4 rounded-xl border border-border-default hover:border-blue-300 hover:bg-blue-50/40 transition group disabled:opacity-50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-txt-primary group-hover:text-blue-700">{tpl.name}</p>
                        <p className="text-xs text-txt-secondary mt-0.5 truncate">{tpl.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-txt-tertiary bg-surface-overlay px-2 py-0.5 rounded-full">{tpl.category}</span>
                          <span className="text-xs font-medium text-emerald-600">
                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(tpl.basePrice)}
                          </span>
                          <span className="text-xs text-txt-tertiary">{tpl.estimatedMinutes} min</span>
                        </div>
                      </div>
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100">
                        {isApplying ? (
                          <svg className="animate-spin text-blue-500" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 11-6.219-8.56"/>
                          </svg>
                        ) : (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                          </svg>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-border-subtle bg-surface-elevated">
              <p className="text-xs text-txt-tertiary text-center">
                Cada plantilla crea un nuevo servicio. Puedes editarlo después desde el panel de servicios.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
